'use client';

import { Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { cn, CustomError, isEnumValue } from '@common/utils';
import { preventSkipToken } from '@common/utils/preventSkipToken';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useMemo } from 'react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

import type { COMMENT_TYPE } from '@/api';
import { COMMENT_SORT_TYPE, LIKE_TYPE } from '@/api';
import { type CommentData, selectCommentList } from '@/api/getCommentList';
import { AuthInfiniteQueryOptions, UnauthInfiniteQueryOptions } from '@/api/v2';
import { ROUTES, SEARCH_PARAMS, SELECTOR } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLike } from '@/hooks/useLike';
import { useAuthStore } from '@/stores/AuthStore';

import { CommentCard } from '../../molecule/CommentCard';
import { CommentSkeletons } from './CommentSkeleton';
import { PointEmptySection } from './PointEmptySection';

interface CommentCardsProps {
  commentType: COMMENT_TYPE;
  isLogin: boolean;
  rootCommentId?: number;
  contentId: number;
  sortType: COMMENT_SORT_TYPE;
  onClickReplyButton?: (rootCommentId: number, parentCommentId: number) => void;
  onClickEditButton?: (commentId: number, isRoot: boolean) => void;
  onClickPickButton?: (commentId: number) => void;
  onChangeCommentData?: (data: CommentData) => void;
  className?: string;
  hasPickButton?: boolean;
}

const SIZE = 50;
const INITIAL_PAGE = 1;

const CommentCards = ({
  isLogin,
  commentType,
  rootCommentId,
  contentId,
  sortType,
  onClickReplyButton,
  onClickEditButton,
  onClickPickButton,
  onChangeCommentData,
  className,
  hasPickButton,
}: CommentCardsProps) => {
  const userId = useAuthStore((state) => state.myInfo?.id);

  if (!isEnumValue(COMMENT_SORT_TYPE, sortType))
    throw new CustomError({ return_message: '댓글을 불러올 수 없습니다.' });

  const commentListQuery = useSuspenseInfiniteQuery({
    ...preventSkipToken(
      (isLogin
        ? AuthInfiniteQueryOptions['get:/wello/v2/auth/wello-comment']
        : UnauthInfiniteQueryOptions['get:/wello/v2/allows/wello-comment'])({
        query: {
          page: INITIAL_PAGE,
          content_id: contentId,
          comment_type_cd: commentType,
          comment_sort: sortType,
          size: SIZE,
          parent_comment_id: rootCommentId,
        },
      }),
    ),
    getNextPageParam: ({ context }) => {
      const { root_total_count, page } = context ?? {};

      if (!root_total_count || !page) return;

      const nextPage = page + 1;
      const maxPage = Math.ceil(root_total_count / SIZE);

      if (maxPage < nextPage) return;

      return nextPage;
    },
    select: ({ pages }) => {
      const lastCommentPage = selectCommentList(
        pages[pages.length - 1].context,
        userId,
      );

      const flatCommentList = pages.flatMap(
        ({ context }) => selectCommentList(context, userId).commentList,
      );

      const uniqueCommentList = flatCommentList.filter(
        (comment, index, self) =>
          index === self.findIndex((t) => t.commentId === comment.commentId),
      );

      return {
        ...lastCommentPage,
        commentList: uniqueCommentList,
      };
    },
    initialPageParam: INITIAL_PAGE,
  });

  const {
    data: { commentList, visibleTotalCount },
    fetchNextPage,
  } = commentListQuery;

  const debouncedFetchNextPage = useMemo(() => {
    return debounce(fetchNextPage, 1_000);
  }, [fetchNextPage]);

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        debouncedFetchNextPage();
      }
    },
  });

  const { like } = useLike();
  const router = useCustomRouter();
  const pathname = usePathname();
  const { toast } = useToast();

  const handleLike = (commentId: number, isLike: boolean) => {
    like({
      contentId: commentId,
      isLike,
      likeType: LIKE_TYPE.COMMENT,
    });
  };

  useEffect(() => {
    if (!onChangeCommentData || !commentListQuery.data) return;

    onChangeCommentData(commentListQuery.data);
  }, [commentListQuery.data, onChangeCommentData]);

  const handleSortType = (sortType: COMMENT_SORT_TYPE) => {
    const url = new URL(window.location.href);
    url.searchParams.set(SEARCH_PARAMS.SORT_TYPE, sortType);
    window.history.replaceState(null, '', url.href);
  };

  const isRootComment = !rootCommentId;
  const isInitialChildrenOpened = commentList.length < 50;

  return (
    <section
      className={cn('bg-Gray-white px-16 py-24', className)}
      id={SELECTOR.COMMENT_LIST_TOP}
    >
      <header className="mb-16 flex items-center justify-between">
        <strong className="my-16 font-600 text-Gray-900 typo-body4">
          댓글{' '}
          {isLogin ? (
            <CountUp preserveValue duration={1} end={visibleTotalCount} />
          ) : (
            <span className="text-Gray-700">??개</span>
          )}
        </strong>
        <div className="flex items-center gap-8 font-500 text-Gray-500 typo-body4">
          <button
            className={cn({
              'text-Gray-800': sortType === COMMENT_SORT_TYPE.LATEST,
            })}
            type="button"
            onClick={() => {
              if (!isLogin) {
                toast({
                  message: '로그인 후 이용해주세요.',
                });

                return;
              }
              handleSortType(COMMENT_SORT_TYPE.LATEST);
            }}
          >
            최신순
          </button>
          <span className="typo-caption1">|</span>
          <button
            className={cn({
              'text-Gray-800': sortType === COMMENT_SORT_TYPE.POPULARITY,
            })}
            type="button"
            onClick={() => {
              if (!isLogin) {
                toast({
                  message: '로그인 후 이용해주세요.',
                });

                return;
              }

              handleSortType(COMMENT_SORT_TYPE.POPULARITY);
            }}
          >
            인기순
          </button>
        </div>
      </header>
      {!isLogin ? (
        <PointEmptySection
          actionButton={{
            text: '회원 가입하기',
            onClick: () =>
              router.push(
                ROUTES.LOGIN.withSearchParams({
                  searchParams: { redirect: pathname ?? '' },
                }),
              ),
          }}
          mainMessage="댓글을 확인하려면? 👀"
          subMessage={`로그인 후 볼 수 있어요.\n지금 회원가입하면 즉시 500원 지급!`}
        />
      ) : null}
      <ul
        className={cn('flex flex-col gap-16', {
          'invisible absolute size-0 overflow-hidden': !isLogin,
        })}
      >
        {commentList.length ? (
          commentList.map((comment) => {
            const { commentId } = comment;

            return (
              <li key={commentId}>
                <CommentCard
                  comment={comment}
                  hasMenu={isLogin}
                  hasPickButton={
                    !comment.isMine && comment.isWelloHelper && hasPickButton
                  }
                  initialChildrenOpened={isInitialChildrenOpened}
                  onClickEditButton={(commentId) => {
                    onClickEditButton?.(commentId, true);
                  }}
                  onClickLikeButton={() =>
                    handleLike(commentId, comment.isMyThumbUp)
                  }
                  onClickPickButton={() => onClickPickButton?.(commentId)}
                  onClickReplyButton={() => {
                    onClickReplyButton?.(commentId, commentId);
                  }}
                >
                  {comment.childCommentList?.length ? (
                    <ul className="ml-52 mt-16 flex flex-col gap-8">
                      {comment.childCommentList.map((childrenComment) => {
                        const { commentId: childrenCommentId } =
                          childrenComment;

                        return (
                          <li key={childrenCommentId}>
                            <CommentCard
                              comment={childrenComment}
                              hasMenu={isLogin}
                              onClickEditButton={(commentId) => {
                                onClickEditButton?.(commentId, false);
                              }}
                              onClickLikeButton={() =>
                                handleLike(
                                  childrenCommentId,
                                  childrenComment.isMyThumbUp,
                                )
                              }
                              onClickReplyButton={() => {
                                onClickReplyButton?.(
                                  commentId,
                                  childrenCommentId,
                                );
                              }}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </CommentCard>
              </li>
            );
          })
        ) : (
          <PointEmptySection
            mainMessage="아직 댓글이 없어요"
            subMessage={`지금 첫 댓글을 남기면,\n바로 출금 가능한 포인트를 드려요!`}
          />
        )}
        {commentListQuery.hasNextPage && isRootComment ? (
          <li ref={ref} className="flex items-center justify-center">
            <Spinner />
          </li>
        ) : null}
      </ul>
    </section>
  );
};

export interface CommentListProps extends Omit<CommentCardsProps, 'isLogin'> {
  initialData?: CommentData;
}

export const CommentList = ({
  initialData,
  ...restProps
}: CommentListProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  return isLogin !== undefined ? (
    <Suspense fallback={<CommentSkeletons initialData={initialData} />}>
      <CommentCards {...restProps} isLogin={isLogin} />
    </Suspense>
  ) : (
    <CommentSkeletons initialData={initialData} />
  );
};
